/**
 * Shared translation utilities for Auth0 screen translations
 *
 * This module provides generic functions for handling translation lookups,
 * nested key navigation, and parameter interpolation across all screen types.
 */

/**
 * Generic function to get a nested translation value from a translations dictionary
 *
 * @param translations - The translations dictionary (e.g., LOGIN_CUSTOM_TRANSLATIONS)
 * @param locale - The locale string (e.g., "en", "es", "en-US")
 * @param key - The dot-notation key path (e.g., "form.fields.username.required")
 * @param params - Optional parameters for string interpolation (e.g., { minLength: 8 })
 * @returns The translated string, or empty string if not found
 */
export function getNestedTranslation<T>(
  translations: Record<string, T>,
  locale: string | undefined,
  key: string,
  params?: Record<string, string | number>
): string {
  const lang = (locale || "en").split("-")[0].toLowerCase();

  const localeTranslations = translations[lang] || translations.en;

  const keys = key.split(".");
  let value: unknown = localeTranslations;

  for (const k of keys) {
    if (value && typeof value === "object" && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      // Fallback to English equivalent
      value = translations.en;
      for (const fallbackKey of keys) {
        if (value && typeof value === "object" && fallbackKey in value) {
          value = (value as Record<string, unknown>)[fallbackKey];
        } else {
          return ""; // Key not found
        }
      }
      break;
    }
  }

  let result = typeof value === "string" ? value : "";

  // Handle parameter interpolation
  if (params && result) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      result = result.replace(`\${${paramKey}}`, String(paramValue));
    });
  }

  return result;
}

/**
 * Generic function to get all translations for a specific locale
 *
 * @param translations - The translations dictionary (e.g., LOGIN_CUSTOM_TRANSLATIONS)
 * @param locale - The locale string (e.g., "en", "es", "en-US")
 * @returns The complete translations object for the locale, falling back to English
 */
export function getAllTranslations<T>(
  translations: Record<string, T>,
  locale: string | undefined
): T {
  const lang = (locale || "en").split("-")[0].toLowerCase();
  return translations[lang] || translations.en;
}
